import * as React from 'react';

import { DataSourceTableSelectItem } from 'src/constants/dataSourceTable';
import { ProjectVirtualGraph } from 'src/store/state/projects';

export type PreservedCustomQueryState = {
  query: string;
  fields: ProjectVirtualGraph['fields'];
  previewRows: ProjectVirtualGraph['previewRows'];
  allResultsIncluded: boolean;
};

export type PreservedTableSelectionState = {
  from: DataSourceTableSelectItem;
  fields: ProjectVirtualGraph['fields'];
  previewRows: ProjectVirtualGraph['previewRows'];
  allResultsIncluded: boolean;
};

type UseQueryModeSwitchParams = {
  localVirtualGraph: ProjectVirtualGraph | null;
  virtualGraph: ProjectVirtualGraph | null | undefined;
  setLocalVirtualGraph: React.Dispatch<
    React.SetStateAction<ProjectVirtualGraph | null>
  >;
};

type UseQueryModeSwitchReturn = {
  submittedCustomQuery: string;
  setSubmittedCustomQuery: React.Dispatch<React.SetStateAction<string>>;
  preservedCustomQueryState: PreservedCustomQueryState | null;
  setPreservedCustomQueryState: React.Dispatch<
    React.SetStateAction<PreservedCustomQueryState | null>
  >;
  preservedTableSelectionState: PreservedTableSelectionState | null;
  setPreservedTableSelectionState: React.Dispatch<
    React.SetStateAction<PreservedTableSelectionState | null>
  >;
  isTableTreeOpen: boolean;
  setIsTableTreeOpen: React.Dispatch<React.SetStateAction<boolean>>;
  expandAllTables: boolean;
  setExpandAllTables: React.Dispatch<React.SetStateAction<boolean>>;
  handleSetIsUsingCustomQuery: (isUsingCustomQuery: boolean) => void;
  initializeFromVirtualGraph: (vg: ProjectVirtualGraph) => void;
};

export const useQueryModeSwitch = ({
  localVirtualGraph,
  virtualGraph,
  setLocalVirtualGraph,
}: UseQueryModeSwitchParams): UseQueryModeSwitchReturn => {
  const [submittedCustomQuery, setSubmittedCustomQuery] = React.useState('');
  const [preservedCustomQueryState, setPreservedCustomQueryState] =
    React.useState<PreservedCustomQueryState | null>(null);
  const [preservedTableSelectionState, setPreservedTableSelectionState] =
    React.useState<PreservedTableSelectionState | null>(null);
  const [isTableTreeOpen, setIsTableTreeOpen] = React.useState(true);
  const [expandAllTables, setExpandAllTables] = React.useState(false);

  const setContents = React.useCallback(
    (update: Partial<ProjectVirtualGraph>) => {
      setLocalVirtualGraph((prev) =>
        prev
          ? {
              ...prev,
              ...update,
              dateLastUpdated: Date.now(),
            }
          : prev
      );
    },
    [setLocalVirtualGraph]
  );

  const initializeFromVirtualGraph = React.useCallback(
    (vg: ProjectVirtualGraph) => {
      if (typeof vg.from === 'string') {
        setSubmittedCustomQuery(vg.from);
        setPreservedCustomQueryState({
          query: vg.from,
          fields: vg.fields,
          previewRows: vg.previewRows,
          allResultsIncluded: vg.allResultsIncluded ?? false,
        });
        setPreservedTableSelectionState(null);
      } else {
        setSubmittedCustomQuery('');
        setPreservedCustomQueryState(null);
        if (vg.from && typeof vg.from === 'object' && vg.from.name) {
          setPreservedTableSelectionState({
            from: vg.from,
            fields: vg.fields,
            previewRows: vg.previewRows,
            allResultsIncluded: vg.allResultsIncluded ?? false,
          });
        } else {
          setPreservedTableSelectionState(null);
        }
      }
    },
    []
  );

  const handleSetIsUsingCustomQuery = React.useCallback(
    (isUsingCustomQuery: boolean) => {
      if (!localVirtualGraph || !virtualGraph) {
        return;
      }

      if (isUsingCustomQuery) {
        if (typeof localVirtualGraph.from === 'string') {
          return;
        }

        // Preserve the current table selection state before switching
        if (
          typeof localVirtualGraph.from === 'object' &&
          localVirtualGraph.from.name
        ) {
          setPreservedTableSelectionState({
            from: localVirtualGraph.from,
            fields: localVirtualGraph.fields,
            previewRows: localVirtualGraph.previewRows,
            allResultsIncluded: localVirtualGraph.allResultsIncluded ?? false,
          });
        }

        // Reset expandAllTables so next switch to table mode can trigger expansion
        setExpandAllTables(false);

        // Restore preserved query state if available
        if (preservedCustomQueryState) {
          setSubmittedCustomQuery(preservedCustomQueryState.query);
          setContents({
            from: preservedCustomQueryState.query,
            fields: preservedCustomQueryState.fields,
            previewRows: preservedCustomQueryState.previewRows,
            allResultsIncluded: preservedCustomQueryState.allResultsIncluded,
          });
        } else {
          setSubmittedCustomQuery('');
          setContents({
            from: '',
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          });
        }
      } else {
        if (typeof localVirtualGraph.from === 'object') {
          return;
        }

        // Preserve the current custom query state before switching
        setPreservedCustomQueryState({
          query: localVirtualGraph.from,
          fields: localVirtualGraph.fields,
          previewRows: localVirtualGraph.previewRows,
          allResultsIncluded: localVirtualGraph.allResultsIncluded ?? false,
        });
        // Reset submitted query when switching back to table mode
        setSubmittedCustomQuery('');
        // Expand table tree and all sub-trees when switching to table mode
        setIsTableTreeOpen(true);
        setExpandAllTables(true);

        // Restore preserved table selection if available
        if (preservedTableSelectionState) {
          setContents({
            from: preservedTableSelectionState.from,
            fields: preservedTableSelectionState.fields,
            previewRows: preservedTableSelectionState.previewRows,
            allResultsIncluded: preservedTableSelectionState.allResultsIncluded,
          });
        } else {
          setContents({
            from: {
              name: '',
              text: '',
              value: '',
            },
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          });
        }
      }
    },
    [
      setContents,
      localVirtualGraph,
      virtualGraph,
      preservedCustomQueryState,
      preservedTableSelectionState,
    ]
  );

  return {
    submittedCustomQuery,
    setSubmittedCustomQuery,
    preservedCustomQueryState,
    setPreservedCustomQueryState,
    preservedTableSelectionState,
    setPreservedTableSelectionState,
    isTableTreeOpen,
    setIsTableTreeOpen,
    expandAllTables,
    setExpandAllTables,
    handleSetIsUsingCustomQuery,
    initializeFromVirtualGraph,
  };
};
